import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useMutation } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { initializeMixpanel } from "@/utils/Analytics/Mixpanel/useInitializeMixpanel/useInitializeMixpanel";
import { setAnalyticsTrackingAllowed } from "@/utils/Analytics/analyticsTrackingConsent";
import { initializeSentry } from "@/utils/sentry/initializeSentry";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  CreateCustomerUserInput,
  useCreateNewCustomerUserMutation,
  useCreateNewCustomerUserMutation$data,
} from "./__generated__/useCreateNewCustomerUserMutation.graphql";

export const useCreateNewCustomerUser = () => {
  const { showToast } = useToast();
  const [createCustomerUserMutation, isCreating] =
    useMutation<useCreateNewCustomerUserMutation>(
      useCreateNewCustomerUserMutationString
    );
  const trackEvent = useTrackEvent();
  const createCustomerUser = (
    input: CreateCustomerUserInput,
    onComplete?: (res: useCreateNewCustomerUserMutation$data) => void,
    onError?: (res: useCreateNewCustomerUserMutation$data) => void
  ) => {
    createCustomerUserMutation({
      variables: { input },
      onCompleted: (res) => {
        if (res.responsePayload?.success) {
          if (input.analyticsTrackingConsent) {
            setAnalyticsTrackingAllowed(true);
            initializeSentry();
            initializeMixpanel();
          }
          trackEvent("AuthModal.CreatedNewCustomerUser", {});
          onComplete?.(res);
        } else {
          trackEvent("AuthModal.CreatedNewCustomerUserError", {
            error: res.responsePayload?.errors,
          });
          onError && onError(res);
        }
      },
      onError: (errors: any) => {
        showToast({
          description: t`Unable to create new user.`,
          semanticStyle: "error",
          toastId: "create-customer-user-error",
        });
        onError && onError(errors);
      },
    });
  };

  return [createCustomerUser, isCreating] as const;
};

export const useCreateNewCustomerUserMutationString = graphql`
  mutation useCreateNewCustomerUserMutation($input: CreateCustomerUserInput!) {
    responsePayload: createCustomerUser(input: $input) {
      success
      session {
        user {
          id
        }
      }
      errors {
        field
        code
        message: code
      }
    }
  }
`;
